import { DateTime } from 'luxon';
import { relativeTimeDescription } from '../utils/date';
import { capitalizeFirstLetter } from '../utils/string';
import { postEndpoint, Service } from '../services/aws';
export const getPasses = async (config, station, location) => {
    const passes = await postEndpoint(config, Service.Stations, 'predict-passes', {
        visible: false,
        lat: location.latitude,
        long: location.longitude,
        intrinsicBrightness: station.intrinsicBrightness,
        tle: `${station.tleLine1},${station.tleLine2}`,
        count: 100
    });
    return passes?.map((pass) => {
        pass.riseTime = DateTime.fromISO(pass.riseTime);
        pass.peakTime = DateTime.fromISO(pass.peakTime);
        pass.setTime = DateTime.fromISO(pass.setTime);
        return pass;
    });
};
export const degToCardinal = (deg) => {
    if (deg > 11.25 && deg < 33.75) {
        return 'N' + 'N' + 'E';
    }
    if (deg > 33.75 && deg < 56.25) {
        return 'E' + 'N' + 'E';
    }
    if (deg > 56.25 && deg < 78.75) {
        return 'E';
    }
    if (deg > 78.75 && deg < 101.25) {
        return 'E' + 'S' + 'E';
    }
    if (deg > 101.25 && deg < 123.75) {
        return 'E' + 'S' + 'E';
    }
    if (deg > 123.75 && deg < 146.25) {
        return 'S' + 'E';
    }
    if (deg > 146.25 && deg < 168.75) {
        return 'S' + 'S' + 'E';
    }
    if (deg > 168.75 && deg < 191.25) {
        return 'S';
    }
    if (deg > 191.25 && deg < 213.75) {
        return 'S' + 'S' + 'W';
    }
    if (deg > 213.75 && deg < 236.25) {
        return 'S' + 'W';
    }
    if (deg > 236.25 && deg < 258.75) {
        return 'W' + 'S' + 'W';
    }
    if (deg > 258.75 && deg < 281.25) {
        return 'W';
    }
    if (deg > 281.25 && deg < 303.75) {
        return 'W' + 'N' + 'W';
    }
    if (deg > 303.75 && deg < 326.25) {
        return 'N' + 'W';
    }
    if (deg > 326.25 && deg < 348.75) {
        return 'N' + 'N' + 'W';
    }
    return 'N';
};
const degToCardinalPlain = (azimuth) => {
    if (azimuth >= 337.5 || azimuth < 22.5)
        return 'north';
    if (azimuth >= 22.5 && azimuth < 67.5)
        return 'northeast';
    if (azimuth >= 67.5 && azimuth < 112.5)
        return 'east';
    if (azimuth >= 112.5 && azimuth < 157.5)
        return 'southeast';
    if (azimuth >= 157.5 && azimuth < 202.5)
        return 'south';
    if (azimuth >= 202.5 && azimuth < 247.5)
        return 'southwest';
    if (azimuth >= 247.5 && azimuth < 292.5)
        return 'west';
    return 'northwest';
};
export const generatePassDescription = (station, pass) => {
    const now = DateTime.now();
    const timeDiff = pass.riseTime.diff(now, 'minutes').minutes;
    const remaining = Math.ceil(pass.setTime.diff(now, 'minutes').minutes);
    const isNow = timeDiff < 1 && timeDiff > -Math.ceil(pass.duration / 60);
    const timeDescription = capitalizeFirstLetter(relativeTimeDescription(pass.riseTime));
    const riseDirection = degToCardinalPlain(pass.riseAzimuth);
    const setDirection = degToCardinalPlain(pass.setAzimuth);
    const durationMinutes = Math.round(pass.duration / 60);
    let brightnessDescription = '';
    if (!pass.visible) {
        brightnessDescription = '';
    }
    else if (pass.brightness < -4) {
        brightnessDescription = 'very bright and ';
    }
    else if (pass.brightness < -2) {
        brightnessDescription = 'bright and ';
    }
    else if (pass.brightness < 0) {
        brightnessDescription = 'not very bright and ';
    }
    else if (pass.brightness >= 0) {
        brightnessDescription = 'dim and ';
    }
    let altitudeDescription = '';
    if (pass.peakAlt > 75) {
        altitudeDescription = 'very high in the sky';
    }
    else if (pass.peakAlt > 55) {
        altitudeDescription = 'high in the sky';
    }
    else if (pass.peakAlt > 25) {
        altitudeDescription = 'low in the sky';
    }
    else {
        altitudeDescription = 'just above the horizon';
    }
    const visibilityDescription = pass.visible
        ? isNow
            ? 'visible now'
            : 'visible overhead'
        : 'overhead but not visible';
    let headline = '';
    let detail = '';
    if (isNow) {
        headline = `The ${station.name} is ${visibilityDescription} for ${remaining} more minute${remaining === 1 ? '' : 's'}`;
        detail = `It's ${brightnessDescription}${altitudeDescription} traveling from the ${riseDirection} to the ${setDirection}`;
    }
    else if (timeDiff > 0) {
        headline = `${timeDescription} the ${station.name} will be ${visibilityDescription} for ${durationMinutes} minute${durationMinutes === 1 ? '' : 's'}`;
        detail = `It will be ${brightnessDescription}${altitudeDescription} traveling from the ${riseDirection} to the ${setDirection}`;
    }
    else {
        headline = `${timeDescription} the ${station.name} was ${visibilityDescription} for ${durationMinutes} minute${durationMinutes === 1 ? '' : 's'}`;
        detail = `It was ${brightnessDescription}${altitudeDescription}, and it traveled from the ${riseDirection} to the ${setDirection}`;
    }
    return { headline, detail };
};
//# sourceMappingURL=pass.js.map